<script lang="ts">
	import { cn } from '$lib/utils.js';
	import { NavigationMenu } from 'bits-ui';
	import { ChevronDown } from 'lucide-svelte';
	import { navDescriptions } from '$lib/config/site';
	import { page } from '$app/state';

	let { navigation } = $props();

	type ListItemProps = {
		className?: string;
		title: string;
		href: string;
		content: string;
	};
</script>

<div class="mr-4 hidden md:flex">
	<nav class="flex items-center gap-6 text-sm">
		{#snippet ListItem({ className, title, content, href }: ListItemProps)}
			<li>
				<NavigationMenu.Link
					class={cn(
						'block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-muted hover:text-accent-foreground focus:bg-muted focus:text-accent-foreground',
						className
					)}
					{href}
				>
					<h4
						class={cn(
							'text-sm font-semibold leading-none',
							page.url.pathname === href ? 'font-semibold underline' : ''
						)}
					>
						{title}
					</h4>
					<p class="line-clamp-2 text-xs leading-snug text-muted-foreground">
						{content}
					</p>
				</NavigationMenu.Link>
			</li>
		{/snippet}

		<NavigationMenu.Root
			delayDuration={150}
			class="relative z-10 flex max-w-max flex-1 items-center justify-center"
		>
			<NavigationMenu.List
				class="group flex flex-1 list-none items-center justify-center space-x-1"
			>
				{#each navigation as nav}
					{#if nav.items?.length}
						<NavigationMenu.Item>
							<NavigationMenu.Trigger
								class="group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-muted hover:text-accent-foreground focus:bg-muted focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-muted/50 data-[state=open]:bg-muted/50"
							>
								{nav.title}
								<ChevronDown
									class="group-data[state=open]:rotate-180 relative top-[1px] ml-1 size-3 transition duration-200"
									aria-hidden="true"
								/>
							</NavigationMenu.Trigger>
							<NavigationMenu.Content
								class="left-0 top-0 z-50 w-full data-[motion^=from-]:animate-in data-[motion^=to-]:animate-out data-[motion^=from-]:fade-in data-[motion^=to-]:fade-out data-[motion=from-end]:slide-in-from-right-52 data-[motion=from-start]:slide-in-from-left-52 data-[motion=to-end]:slide-out-to-right-52 data-[motion=to-start]:slide-out-to-left-52 md:absolute md:w-auto"
							>
								<ul class="grid gap-3 p-6 md:w-[400px] lg:w-[500px] lg:grid-cols-[.75fr_1fr]">
									<!-- <li class="row-span-3"> -->
									<!-- 	<NavigationMenu.Link -->
									<!-- 		href="/" -->
									<!-- 		class="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-muted/50 to-muted p-6 no-underline outline-none focus:shadow-md" -->
									<!-- 	> -->
									<!-- 		<Icons.logo class="h-6 w-6" /> -->
									<!-- 		<div class="mb-2 mt-4 text-lg font-medium">MVMNT</div> -->
									<!-- 		<p class="text-sm leading-tight text-muted-foreground"> -->
									<!-- 			The best TMS for brokers. -->
									<!-- 		</p> -->
									<!-- 	</NavigationMenu.Link> -->
									<!-- </li> -->
									{#each nav.items as item}
										{@render ListItem({
											href: item.externalPath ?? `/${item.path}`,
											title: item.title,
											content: navDescriptions[item.title]
										})}
									{/each}
								</ul>
							</NavigationMenu.Content>
						</NavigationMenu.Item>
					{:else}
						<NavigationMenu.Item>
							<NavigationMenu.Link
								class={cn(
									'group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:bg-muted hover:text-accent-foreground focus:bg-muted focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-muted/50 data-[state=open]:bg-muted/50',
									page.url.pathname === `/${nav.path}` ? 'font-semibold underline' : ''
								)}
								href={`/${nav.path}`}
							>
								{nav.title}
							</NavigationMenu.Link>
						</NavigationMenu.Item>
					{/if}
				{/each}

				<NavigationMenu.Indicator
					class="top-full z-[1] flex h-1.5 items-end justify-center overflow-hidden data-[state=visible]:animate-in data-[state=hidden]:animate-out data-[state=hidden]:fade-out data-[state=visible]:fade-in"
				>
					<div class="relative top-[60%] size-2 rotate-45 rounded-tl-sm bg-border shadow-md"></div>
				</NavigationMenu.Indicator>
			</NavigationMenu.List>
			<div class="absolute left-0 top-full flex justify-center">
				<NavigationMenu.Viewport
					class="origin-top-center relative mt-1.5 h-[var(--bits-navigation-menu-viewport-height)] w-full overflow-hidden rounded-md border bg-background text-popover-foreground shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-90 md:w-[var(--bits-navigation-menu-viewport-width)]"
				/>
			</div>
		</NavigationMenu.Root>
	</nav>
</div>
